import React, { useState, useEffect } from 'react';
import { useOrg } from '../../../hooks/useOrg';
import { useAuth } from '../../../hooks/useAuth';
import { useLocations } from '../../../hooks/useLocations';
import { api } from '../../../lib/api';
import ConfirmDialog from '../../shared/ConfirmDialog';
import { MapPin, Check } from 'lucide-react';

// Which offices a member may see. Owners and admins always see every
// office. Anyone else with offices ticked here sees only those — their
// location switcher, calendar and referral inbox are limited to them;
// nothing ticked means no restriction.
function OfficeAccess({ member, locations, onChange }) {
  const [saving, setSaving] = useState(false);
  const allAccess = ['owner', 'admin'].includes(member.role);
  const ids = member.location_ids || [];
  if (allAccess) return <span className="text-[11px] text-muted-c inline-flex items-center gap-1"><MapPin size={10} /> All locations</span>;
  const toggle = async (locId) => {
    const next = ids.includes(locId) ? ids.filter((x) => x !== locId) : [...ids, locId];
    setSaving(true);
    try { await api.setStaffLocations(member.org_id, member.user.id, next); onChange(next); }
    catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {locations.map((l) => {
        const on = ids.includes(l.id);
        return (
          <button key={l.id} onClick={() => toggle(l.id)} disabled={saving} title={on ? `Can see ${l.name}` : `Give access to ${l.name}`}
            className={`inline-flex items-center gap-1 px-2 py-[3px] rounded-md text-[10.5px] font-semibold border transition-all disabled:opacity-60 ${on ? 'bg-accent-glow text-accent-light border-accent-light' : 'bg-tertiary text-muted-c border-theme'}`}>
            {on && <Check size={9} />} {l.name}
          </button>
        );
      })}
      {ids.length === 0 && <span className="text-[10.5px] text-muted-c ml-1">all (none assigned)</span>}
    </div>
  );
}

const ORG_ROLES = [
  { value: 'admin', label: 'Admin', description: 'Full access to settings, members, and data' },
  { value: 'editor', label: 'Editor', description: 'Can create and edit records but not manage settings' },
  { value: 'medical_provider', label: 'Medical Provider', description: 'A treating provider — bookable on the calendar at their offices', medicalOnly: true },
];

export default function TeamTab() {
  const { currentOrg } = useOrg();
  const { profile } = useAuth();
  const { locations } = useLocations();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'editor', message: '', location_ids: [] });
  const [sending, setSending] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [invites, setInvites] = useState([]);     // outstanding invitations
  const [inviteBusy, setInviteBusy] = useState(null); // invitation id being resent/revoked
  const [inviteNote, setInviteNote] = useState({}); // id → "Resent" after a resend

  const fetchMembers = async () => {
    if (!currentOrg) return;
    try {
      const [data, pending] = await Promise.all([
        api.getOrgMembers(currentOrg.id),
        api.getOrgInvitations(currentOrg.id).catch(() => []),
      ]);
      setMembers(data.filter(m => m.role !== 'client'));
      setInvites(pending || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resendInvite = async (inv) => {
    setInviteBusy(inv.id);
    try { await api.resendInvitation(currentOrg.id, inv.id); setInviteNote((n) => ({ ...n, [inv.id]: 'Resent' })); await fetchMembers(); }
    catch (err) { alert(err.message); }
    finally { setInviteBusy(null); }
  };
  const revokeInvite = async (inv) => {
    if (!window.confirm(`Cancel the invitation to ${inv.email}? The link in their email will stop working.`)) return;
    setInviteBusy(inv.id);
    try { await api.revokeInvitation(currentOrg.id, inv.id); await fetchMembers(); }
    catch (err) { alert(err.message); }
    finally { setInviteBusy(null); }
  };

  useEffect(() => { fetchMembers(); }, [currentOrg]); // eslint-disable-line

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviteError('');
    setInviteSuccess('');
    setSending(true);

    try {
      await api.sendInvitation(currentOrg.id, {
        email: inviteForm.email,
        role: inviteForm.role,
        personal_message: inviteForm.message || null,
        location_ids: inviteForm.role === 'admin' ? [] : inviteForm.location_ids,
      });
      setInviteSuccess(`Invitation sent to ${inviteForm.email}`);
      setInviteForm({ email: '', role: 'editor', message: '', location_ids: [] });
      fetchMembers();
      setTimeout(() => { setShowInvite(false); setInviteSuccess(''); }, 2000);
    } catch (err) {
      setInviteError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleRemove = async () => {
    if (!confirmRemove || !currentOrg) return;
    try {
      await api.removeOrgMember(currentOrg.id, confirmRemove.id);
      setConfirmRemove(null);
      await fetchMembers();
    } catch (err) {
      console.error(err);
    }
  };

  const handleRoleChange = async (memberId, newRole) => {
    try {
      await api.updateOrgMember(currentOrg.id, memberId, { role: newRole });
      setMembers(prev => prev.map(m => m.id === memberId ? { ...m, role: newRole } : m));
    } catch (err) {
      console.error(err);
    }
  };

  const inputClass = 'w-full px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13, 167, 202,0.15)]';

  return (
    <>
      {/* Invite form */}
      {showInvite && (
        <div className="bg-elevated border border-theme rounded-xl p-5 mb-4 animate-fade-in">
          <h3 className="text-[15px] font-semibold text-primary-c mb-4">Invite Team Member</h3>

          {inviteError && <div className="px-4 py-3 rounded-[10px] bg-danger-soft text-danger text-[13px] font-medium mb-4">{inviteError}</div>}
          {inviteSuccess && <div className="px-4 py-3 rounded-[10px] bg-success-soft text-success text-[13px] font-medium mb-4">{inviteSuccess}</div>}

          <form onSubmit={handleInvite} className="space-y-4">
            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-2">Email Address *</label>
              <input type="email" className={inputClass} value={inviteForm.email} onChange={e => setInviteForm(p => ({ ...p, email: e.target.value }))} required placeholder="colleague@example.com" autoFocus />
            </div>

            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-2">Role *</label>
              <div className="grid grid-cols-2 gap-2">
                {ORG_ROLES.filter((r) => !r.medicalOnly || currentOrg?.type === 'medical_practice').map(r => (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => setInviteForm(p => ({ ...p, role: r.value }))}
                    className={`text-left p-3 rounded-[10px] border transition-all ${
                      inviteForm.role === r.value
                        ? 'border-[#0DA7CA] bg-accent-glow shadow-[0_0_0_3px_rgba(13, 167, 202,0.15)]'
                        : 'border-strong bg-tertiary hover:bg-hover-surface'
                    }`}
                  >
                    <div className={`text-[13px] font-semibold ${inviteForm.role === r.value ? 'text-accent-light' : 'text-primary-c'}`}>{r.label}</div>
                    <div className="text-[11px] text-muted-c mt-0.5">{r.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {locations.length > 1 && (
              <div>
                <label className="block text-[12px] font-semibold text-secondary-c mb-1">Locations they can see</label>
                {inviteForm.role === 'admin' ? (
                  <p className="text-[12px] text-muted-c">Admins see every location.</p>
                ) : (
                  <>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {locations.map((l) => {
                        const on = inviteForm.location_ids.includes(l.id);
                        return (
                          <button key={l.id} type="button"
                            onClick={() => setInviteForm((p) => ({ ...p, location_ids: on ? p.location_ids.filter((x) => x !== l.id) : [...p.location_ids, l.id] }))}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] text-[12px] font-semibold border transition-all ${on ? 'bg-accent-glow text-accent-light border-accent-light' : 'bg-tertiary text-muted-c border-theme hover:text-primary-c'}`}>
                            <MapPin size={11} /> {l.name}{on && <Check size={11} />}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[11px] text-muted-c mt-1.5">
                      {inviteForm.location_ids.length === 0
                        ? 'None selected — they will see every location. Pick one or more to limit them.'
                        : `Their calendar, referrals and location switcher will be limited to ${inviteForm.location_ids.length === 1 ? 'this office' : 'these offices'}. Changeable later from this list.`}
                    </p>
                  </>
                )}
              </div>
            )}

            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-2">Personal Message (optional)</label>
              <textarea className={inputClass} rows={2} value={inviteForm.message} onChange={e => setInviteForm(p => ({ ...p, message: e.target.value }))} placeholder="Add a note..." />
            </div>

            <div className="flex justify-end gap-2.5 pt-3 border-t border-theme">
              <button type="button" onClick={() => { setShowInvite(false); setInviteError(''); setInviteSuccess(''); }} className="px-4 py-2 rounded-[10px] text-[13px] font-semibold text-primary-c bg-tertiary border border-theme hover:bg-hover-surface transition-all">Cancel</button>
              <button type="submit" disabled={sending} className="px-4 py-2 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow transition-all disabled:opacity-50">
                {sending ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Outstanding invitations */}
      {invites.length > 0 && (
        <div className="bg-elevated rounded-xl border border-theme overflow-hidden mb-4">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-theme">
            <span className="text-[13px] font-semibold text-secondary-c">{invites.length} pending invitation{invites.length !== 1 ? 's' : ''}</span>
            <span className="text-[11px] text-muted-c">Links expire after 7 days — resend to refresh</span>
          </div>
          <div className="divide-y divide-[var(--border)]">
            {invites.map((inv) => {
              const offices = (inv.location_ids || []).map((id) => locations.find((l) => l.id === id)?.name).filter(Boolean);
              const busy = inviteBusy === inv.id;
              return (
                <div key={inv.id} className="flex items-center justify-between gap-3 px-5 py-3 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-[34px] h-[34px] rounded-[10px] bg-tertiary border border-dashed border-strong flex items-center justify-center text-[11px] font-semibold text-muted-c flex-shrink-0">
                      {(inv.email || '?')[0].toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="text-[13px] font-semibold text-primary-c truncate">{inv.email}</div>
                      <div className="text-[11px] text-muted-c flex items-center gap-1.5 flex-wrap">
                        <span className="capitalize">{String(inv.role).replace('_', ' ')}</span>
                        {offices.length > 0 && <span>· <MapPin size={9} className="inline -mt-0.5" /> {offices.join(', ')}</span>}
                        {!offices.length && ['owner', 'admin'].includes(inv.role) === false && locations.length > 1 && <span>· all locations</span>}
                        <span>· invited {new Date(inv.created_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}{inv.inviter ? ` by ${inv.inviter.first_name}` : ''}</span>
                        {inv.expired
                          ? <span className="text-danger font-semibold">· expired</span>
                          : <span>· expires {new Date(inv.expires_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={() => resendInvite(inv)} disabled={busy} className="text-[12px] font-semibold text-accent-light hover:text-accent disabled:opacity-50">
                      {inviteNote[inv.id] || (inv.expired ? 'Resend' : 'Resend email')}
                    </button>
                    <button onClick={() => revokeInvite(inv)} disabled={busy} className="text-[12px] font-medium text-muted-c hover:text-danger disabled:opacity-50">Cancel</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Members list */}
      <div className="bg-elevated rounded-xl border border-theme overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-theme">
          <span className="text-[13px] font-semibold text-secondary-c">{members.length} member{members.length !== 1 ? 's' : ''}</span>
          {!showInvite && (
            <button onClick={() => setShowInvite(true)} className="flex items-center gap-1.5 text-[12px] font-semibold text-accent-light hover:text-accent transition-colors">
              <span className="text-[15px] leading-none">+</span> Invite Member
            </button>
          )}
        </div>

        {loading ? (
          <div className="px-5 py-8 text-center text-[13px] text-muted-c">Loading...</div>
        ) : members.length === 0 ? (
          <div className="px-5 py-12 text-center text-[13px] text-muted-c">No team members yet.</div>
        ) : (
          <div className="divide-y divide-[var(--border)]">
            {members.map(m => {
              const isYou = m.user?.id === profile?.id;
              const initials = `${(m.user?.first_name || '?')[0]}${(m.user?.last_name || '?')[0]}`.toUpperCase();
              return (
                <div key={m.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-3.5 hover:bg-hover-surface transition-all">
                  <div className="flex items-center gap-3">
                    <div className="w-[34px] h-[34px] rounded-[10px] gradient-accent flex items-center justify-center text-[11px] font-semibold text-white flex-shrink-0">
                      {initials}
                    </div>
                    <div>
                      <div className="text-[13px] font-semibold text-primary-c">
                        {m.user?.first_name} {m.user?.last_name}
                        {isYou && <span className="text-[11px] text-muted-c ml-1.5">(you)</span>}
                      </div>
                      <div className="text-[11px] text-muted-c">{m.user?.email}</div>
                      {locations.length > 1 && m.user && (
                        <div className="mt-1.5">
                          <OfficeAccess member={m} locations={locations} onChange={(ids) => setMembers((prev) => prev.map((x) => (x.id === m.id ? { ...x, location_ids: ids } : x)))} />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 w-full sm:w-auto">
                    <select
                      value={m.role}
                      onChange={e => handleRoleChange(m.id, e.target.value)}
                      disabled={isYou}
                      className="flex-1 sm:flex-none px-2.5 py-1.5 bg-tertiary border border-strong rounded-lg text-[12px] text-primary-c font-medium focus:outline-none focus:border-[#0DA7CA] disabled:opacity-50"
                    >
                      <option value="admin">Admin</option>
                      <option value="editor">Editor</option>
                      <option value="attorney">Attorney</option>
                      <option value="paralegal">Paralegal</option>
                      <option value="medical_provider">Medical Provider</option>
                    </select>
                    {!isYou && m.role !== 'client' && (
                      <button onClick={() => setConfirmRemove(m)} className="text-[12px] font-medium text-muted-c hover:text-danger transition-colors">Remove</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmDialog open={!!confirmRemove} title="Remove Team Member"
        message={`Remove ${confirmRemove?.user?.first_name} ${confirmRemove?.user?.last_name} from the organization?`}
        confirmLabel="Remove" danger onConfirm={handleRemove} onCancel={() => setConfirmRemove(null)} />
    </>
  );
}
